// src/stages/stage1Plant/fern/Fern.jsx
import React, { useMemo, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const FERN_MODEL_PATH = "/stageAssets/stage1-plant/FernPlant.glb";

export default function Fern({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  isMain = false,
  openAmount = 1,
  windStrength = 0, // NEW: storm intensity passed down from FernSystem
}) {
  const { scene } = useGLTF(FERN_MODEL_PATH);
  const groupRef = useRef();
  const shaderRefs = useRef([]);

  const preparedScene = useMemo(() => {
    const clonedScene = scene.clone(true);
    shaderRefs.current = [];

    clonedScene.traverse((obj) => {
      if (!obj.isMesh || !obj.material) return;

      obj.castShadow = true;
      obj.receiveShadow = true;

      const baseMat = Array.isArray(obj.material)
        ? obj.material[0]
        : obj.material;

      const mat = baseMat.clone();

      if (mat.map) {
        mat.map.colorSpace = THREE.SRGBColorSpace;
      }

      mat.transparent = true;
      mat.alphaTest = 0.2;
      mat.side = THREE.DoubleSide;

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uOpenAmount = { value: 1.0 };
        shader.uniforms.uCurlStrength = { value: 0.22 };

        shader.vertexShader =
          `
          uniform float uOpenAmount;
          uniform float uCurlStrength;
          ` + shader.vertexShader;

        shader.vertexShader = shader.vertexShader.replace(
          "#include <begin_vertex>",
          `
          #include <begin_vertex>

          float closeAmount = 1.0 - uOpenAmount;

          // radial distance from center of the plant in local space
          float radial = length(position.xz);

          // normalized influence: farther from center = more movement
          float radialInfluence = smoothstep(0.05, 0.45, radial);

          // top / outer leaf tips move more than inner base
          float verticalInfluence = smoothstep(-0.05, 0.25, position.y);

          float influence = radialInfluence * verticalInfluence;

          // pull inward toward center
          vec2 inwardDir = normalize(-position.xz + vec2(0.0001));
          transformed.xz += inwardDir * closeAmount * influence * uCurlStrength;

          // raise slightly when closing so it feels like curling upward
          transformed.y += closeAmount * influence * 0.08;
          `
        );

        mat.userData.shader = shader;
        shaderRefs.current.push(shader);
      };

      mat.needsUpdate = true;
      obj.material = mat;
    });

    return clonedScene;
  }, [scene]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;

    // EXISTING: smooth shader-based open/close curl
    shaderRefs.current.forEach((shader) => {
      if (!shader?.uniforms?.uOpenAmount) return;

      shader.uniforms.uOpenAmount.value = THREE.MathUtils.lerp(
        shader.uniforms.uOpenAmount.value,
        openAmount,
        0.08
      );
    });

    // NEW: whole-fern storm sway
    // This stacks on top of the fern's original placed rotation.
    if (groupRef.current) {
      const swayX =
        Math.sin(t * 1.7) * 0.02 * windStrength +
        Math.sin(t * 4.2) * 0.008 * windStrength;

      const swayZ =
        Math.cos(t * 1.2) * 0.012 * windStrength +
        Math.sin(t * 3.4) * 0.006 * windStrength;

      groupRef.current.rotation.x = rotation[0] + swayX;
      groupRef.current.rotation.y = rotation[1];
      groupRef.current.rotation.z = rotation[2] + swayZ;
    }
  });

  return (
    <primitive
      ref={groupRef}
      object={preparedScene}
      position={position}
      scale={scale}
      // NOTE:
      // We do NOT pass rotation here anymore because the animated wind sway
      // is now controlling the groupRef rotation every frame.
    />
  );
}

useGLTF.preload(FERN_MODEL_PATH);